import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import { useMemo } from 'react';
import { formatToTwoDecimals, formatDate, format_Date } from 'utils';
import { Logo } from 'assets/svgs';

const useStyles = () =>
  useMemo(
    () =>
      StyleSheet.create({
        page: {
          fontSize: 9,
          lineHeight: 1.6,
          backgroundColor: '#FFFFFF',
          textTransform: 'capitalize',
          padding: '40px 24px 120px 24px',
        },
        gridContainer: {
          flexDirection: 'row',
          justifyContent: 'space-between',
        },
        table: {
          display: 'flex',
          width: 'auto',
        },
        tableHeader: {
          padding: '4px 0', // Reduced padding for a tighter look
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderTopWidth: 1,
          borderStyle: 'solid',
          borderColor: '#DFE3E8',
          backgroundColor: '#E9ECEF', // Light gray background
          alignItems: 'center', // Vertically center the header text
        },
        tableRow: {
          padding: '6px 0', // Slightly reduced padding for rows
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderStyle: 'solid',
          borderColor: '#DFE3E8',
        },
        noBorder: {
          paddingTop: 8,
          paddingBottom: 0,
          borderBottomWidth: 0,
        },
        tableCell: {
          width: '10%', // 10 columns, so each takes 10% of the width
          paddingRight: 4, // Reduced padding for tighter spacing
          paddingLeft: 4, // Consistent left padding
        },
        sectionTitle: {
          fontSize: 12,
          fontWeight: 700,
          marginTop: 16,
          marginBottom: 8,
        },
        subtitle1: {
          fontSize: 10,
          fontWeight: 700,
        },
        subtitle2: {
          fontSize: 7, // Reduced font size to fit text properly
          fontWeight: 700,
          textTransform: 'uppercase', // Uppercase for a professional look
          lineHeight: 1.2, // Reduced line height to prevent wrapping issues
        },
        body1: {
          fontSize: 8, // Slightly reduced font size for table content
        },
        body2: {
          fontSize: 9,
        },
        alignRight: {
          textAlign: 'right',
        },
        mb4: { marginBottom: 4 },
        mb8: { marginBottom: 8 },
        mb40: { marginBottom: 40 },
        logo: {
          width: 100, // Adjust the width as needed
          height: 50, // Adjust the height as needed
          objectFit: 'contain', // Ensures the image scales properly
        },
      }),
    []
  );

const PdfViewerModal = ({ user, data, from, to }) => {
  const styles = useStyles();
  console.log(data);

  // Extract advisor name from name
  const advisorName = data?.name || 'N/A';

  // Payment period based on the selected date in the Paycheck Statement Section
  const paymentPeriod = `${format_Date(from)} - ${format_Date(to)}`;

  // Filter transactions based on the criteria
  const transactions = data?.collaborations || [];

  // Initial Fees: Transactions with "Initial" in IncomeType
  const initialFees = transactions.filter(item =>
    item?.incomeType?.toLowerCase().includes('initial')
  );

  // Client Fees: Transactions with "Fee/Retainer (Client Paid)" in IncomeType
  const clientFees = transactions.filter(
    item => item?.incomeType === 'Fee/Retainer (Client Paid)'
  );

  // Ongoing Fees: Transactions that don't match other criteria
  const otherSections = [
    ...initialFees,
    ...clientFees,
    ...(data?.collaborations || []),
    ...(data?.transferredTransactions || []),
  ];
  const ongoingFees = transactions.filter(
    item => !otherSections.includes(item)
  );

  // Collaborations: From the Collaborations Page
  const collaborations = data?.collaborations || [];

  // Transferred Transactions: From the Transfer Page
  const transferredTransactions = data?.transferredTransactions || [];

  // Adjustments: From the Adjustments Page
  const adjustments = data?.adjustments || [];

  // Total Fees Due: Sum of all payable amounts
  const totalFeesDue = transactions.reduce(
    (sum, item) => sum + (parseFloat(item?.mainAdvisorFinalCommission) || 0),
    0
  );

  // Total adjustment Due: Sum of all payable amounts
  const totalAdjustment = adjustments.reduce(
    (sum, item) => sum + (parseFloat(item?.value) || 0),
    0
  );

  // Render table rows for each section
  const renderTableRows = items =>
    items.map((item, index) => (
      <View style={styles.tableRow} key={index}>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.clientName || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.transactionId || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>
            {formatDate(item?.paymentDate) || 'N/A'}
          </Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.provider || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.planType || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.planNo || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.premiumInvestment || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>{item?.feeType || 'N/A'}</Text>
        </View>
        <View style={styles.tableCell}>
          <Text style={styles.body1}>
            {item?.commissionAmountGross > 0
              ? item?.commissionAmountGross
              : item?.commissionAmountNet || 'N/A'}
          </Text>
        </View>
        <View style={[styles.tableCell, styles.alignRight]}>
          <Text style={styles.body1}>
            {formatToTwoDecimals(item?.mainAdvisorFinalCommission) || '0.00'}
          </Text>
        </View>
      </View>
    ));

  // Render table header for sections with 10 columns
  const renderTableHeader = () => (
    <View style={styles.tableHeader}>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Client</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>IO Ref</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Payment Date</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Provider</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Plan Type</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Plan No.</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Premium/Investment</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>Fee Type</Text>
      </View>
      <View style={styles.tableCell}>
        <Text style={styles.subtitle2}>FCI</Text>
      </View>
      <View style={[styles.tableCell, styles.alignRight]}>
        <Text style={styles.subtitle2}>Payable</Text>
      </View>
    </View>
  );

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        {/* Header Section */}
        <View style={[styles.gridContainer, styles.mb40]}>
          <View>
            <Text style={styles.sectionTitle}>Statement Instructions</Text>
            <Text style={[styles.subtitle1, styles.mb4]}>
              Advisor Name: {advisorName}
            </Text>
            <Text style={[styles.subtitle1, styles.mb4]}>
              Payment Period: {paymentPeriod}
            </Text>
          </View>
          <View>
            {/* <Text style={styles.body2}>Company Logo</Text> */}
            <Image src={Logo} style={styles.logo} alt="Logo" />
          </View>
        </View>

        {/* Table Sections */}
        <View style={styles.table}>
          {/* Initial Fees */}
          <Text style={styles.sectionTitle}>Initial Fees</Text>
          {renderTableHeader()}
          {renderTableRows(initialFees)}

          {/* Ongoing Fees */}
          <Text style={styles.sectionTitle}>Ongoing Fees</Text>
          {renderTableHeader()}
          {renderTableRows(ongoingFees)}

          {/* Client Fees */}
          <Text style={styles.sectionTitle}>Client Fees</Text>
          {renderTableHeader()}
          {renderTableRows(clientFees)}

          {/* Collaborations */}
          <Text style={styles.sectionTitle}>Collaborations</Text>
          {renderTableHeader()}
          {renderTableRows(collaborations)}

          {/* Transferred Transactions */}
          <Text style={styles.sectionTitle}>Transferred Transactions</Text>
          {renderTableHeader()}
          {renderTableRows(transferredTransactions)}

          {/* Adjustments */}
          <Text style={styles.sectionTitle}>Adjustments</Text>
          <View style={styles.tableHeader}>
            <View style={{ width: '90%' }}>
              <Text style={styles.subtitle2}>Adjustment Details</Text>
            </View>
            <View style={[styles.tableCell, styles.alignRight]}>
              <Text style={styles.subtitle2}>Amount</Text>
            </View>
          </View>
          {adjustments.map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <View style={{ width: '90%' }}>
                <Text style={styles.body1}>{item?.name || 'N/A'}</Text>
              </View>
              <View style={[styles.tableCell, styles.alignRight]}>
                <Text style={styles.body1}>
                  {formatToTwoDecimals(item?.value) || '0.00'}
                </Text>
              </View>
            </View>
          ))}

          {/* Total Fees Due */}
          <Text style={styles.sectionTitle}>Total Fees Due</Text>
          <View style={styles.tableHeader}>
            <View style={{ width: '90%' }} />
            <View style={[styles.tableCell, styles.alignRight]}>
              <Text style={styles.subtitle2}>Total</Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={{ width: '90%' }} />
            <View style={[styles.tableCell, styles.alignRight]}>
              <Text style={styles.body1}>
                {formatToTwoDecimals(totalFeesDue + totalAdjustment) || '0.00'}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PdfViewerModal;
